import { ToggleButton, ToggleButtonGroup, Box } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { useCanAccessMarketplace } from "@/lib/features";

/**
 * Segmented control at the top of the Bots-family pages — switches
 * between peer *views* of the bot workflow without bouncing through
 * the main nav.
 *
 * Views:
 *   • "My Bots"     → /bots
 *   • "Marketplace" → /marketplace
 *   • "Testing"     → /testing
 *   • "Lists"       → /lists
 *
 * Why view-level tabs instead of a segmented store: each view keeps
 * its own URL, its own scroll position, and its own deep-link — so
 * refreshing the page on the Testing tab keeps you on Testing, and
 * sharing `/marketplace` works without a query string.
 *
 * The component name is historical — started out as a two-option
 * "Bots ↔ Marketplace" toggle and Testing was layered on later when
 * Sergey asked for it. Kept the filename to minimise churn; the
 * ARIA-level intent is "Bots view selector".
 *
 * Marketplace visibility is gated by `useCanAccessMarketplace()` (a
 * subscription-tier check that returns true in dev). When it returns
 * false the Marketplace segment is dropped from the group — we don't
 * render a disabled state because that would leak the existence of a
 * paywalled feature to users on tiers that can't use it. Testing is
 * always visible today; if entitlement rules appear later, mirror the
 * marketplace pattern.
 *
 * Exclusive ToggleButtonGroup emits `null` if the user taps the
 * already-selected button; we ignore that so the active state never
 * flickers off.
 */

type View = "bots" | "marketplace" | "testing" | "lists";

const PATH_FOR: Record<View, string> = {
  bots: "/bots",
  marketplace: "/marketplace",
  testing: "/testing",
  lists: "/lists"
};

/**
 * Derive the current view from the URL. `/bots` and anything under
 * it (e.g. `/bots/:id`) counts as "bots"; same deal for the other
 * three. Anywhere else (should not happen — this component is only
 * mounted on those four pages) falls back to "bots".
 */
function currentView(pathname: string): View {
  if (pathname.startsWith("/marketplace")) return "marketplace";
  if (pathname.startsWith("/testing")) return "testing";
  if (pathname.startsWith("/lists")) return "lists";
  return "bots";
}

export function BotsMarketplaceToggle() {
  const navigate = useNavigate();
  const location = useLocation();
  const canAccessMarketplace = useCanAccessMarketplace();

  const current = currentView(location.pathname);

  // Build the visible option set. Marketplace is conditionally dropped
  // when the user's tier can't see it — if the user is currently on
  // /marketplace anyway (shouldn't happen while gated, but defensively)
  // we keep it so the active segment doesn't disappear out from under
  // them mid-navigation.
  const views: View[] = ["bots"];
  if (canAccessMarketplace || current === "marketplace") {
    views.push("marketplace");
  }
  views.push("testing");
  views.push("lists");

  // Hide the whole toggle if there's only one option left — prevents
  // a pointless single-segment pill from rendering on tiers that see
  // only "My Bots". Today we always ship at least two (bots + testing),
  // so this branch is defensive.
  if (views.length < 2) return null;

  return (
    <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
      <ToggleButtonGroup
        exclusive
        value={current}
        onChange={(_, v: View | null) => {
          if (!v || v === current) return;
          navigate(PATH_FOR[v]);
        }}
        size="small"
        sx={{
          // Pill-shaped group matching the V2 chip aesthetic.
          //
          // Four segments don't quite fit on a 375pt iPhone SE at the
          // old px: 2.5 padding, so we tighten horizontal padding and
          // font-size on xs. Once we hit the sm breakpoint the wider
          // per-segment padding kicks back in — desktop stays roomy.
          bgcolor: (theme) => theme.palette.action.hover,
          borderRadius: 999,
          p: 0.5,
          "& .MuiToggleButton-root": {
            border: 0,
            borderRadius: "999px !important",
            px: { xs: 1.5, sm: 2.5 },
            py: 0.75,
            fontSize: { xs: "0.78rem", sm: "0.8125rem" },
            whiteSpace: "nowrap",
            textTransform: "none",
            fontWeight: 600,
            color: "text.secondary",
            "&.Mui-selected": {
              bgcolor: "background.paper",
              color: "text.primary",
              boxShadow: 1,
              "&:hover": { bgcolor: "background.paper" }
            }
          }
        }}
      >
        {views.includes("bots") && (
          <ToggleButton value="bots">My Bots</ToggleButton>
        )}
        {views.includes("marketplace") && (
          <ToggleButton value="marketplace">Marketplace</ToggleButton>
        )}
        {views.includes("testing") && (
          <ToggleButton value="testing">Testing</ToggleButton>
        )}
        {views.includes("lists") && (
          <ToggleButton value="lists">Lists</ToggleButton>
        )}
      </ToggleButtonGroup>
    </Box>
  );
}
